import * as m from '@staiter/i18n';
import type { PeriodCloseBlocker } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// S6 — Period Close wizard (Select period → Checklist → Review → Lock).
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Lock as LockIcon } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, DateInput, Input } from '~/components/primitives';
import { fetchPeriodClose, lockPeriod } from '~/lib/api/period-close';
import shared from './_auth._shared.module.css';
import styles from './_auth.period-close.module.css';

export const Route = createFileRoute('/_auth/period-close')({ component: PeriodClose });

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

type Step = 'select' | 'checklist' | 'review' | 'lock';
const STEPS: { id: Step }[] = [
  { id: 'select' },
  { id: 'checklist' },
  { id: 'review' },
  { id: 'lock' },
];

function PeriodClose(): JSX.Element {
  const [period, setPeriod] = useState<string>(currentPeriod());
  const [step, setStep] = useState<Step>('select');
  const [note, setNote] = useState<string>('');
  const toast = useToast();
  const qc = useQueryClient();

  const checklist = useQuery({
    queryKey: ['period-close', period],
    queryFn: () => fetchPeriodClose(period),
    enabled: step !== 'select',
    staleTime: 15_000,
  });

  const lockMut = useMutation({
    mutationFn: () => lockPeriod(period, note.trim() || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['period-close', period] });
      toast.push({ variant: 'success', title: m.route_pc_toast_locked(), description: period });
      setStep('lock');
    },
    onError: (err: Error) => {
      toast.push({
        variant: 'error',
        title: m.route_pc_toast_lock_failed(),
        description: err.message,
      });
    },
  });

  const canLock = checklist.data?.can_lock ?? false;
  const isLocked = checklist.data?.state === 'locked';

  return (
    <div className={shared.page}>
      <PageHeader title={m.nav_period_close()} description={m.route_pc_description()} />

      <Stepper current={step} />

      {step === 'select' && (
        <section className={styles.card}>
          <h2 className={shared.sectionHeading}>{m.route_pc_select_heading()}</h2>
          <div className={styles.selectRow}>
            <DateInput
              label={m.route_pc_period_label()}
              mode="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value || currentPeriod())}
              hint={m.route_pc_period_hint()}
            />
            <Button onClick={() => setStep('checklist')} disabled={!period}>
              {m.route_pc_btn_next()}
            </Button>
          </div>
        </section>
      )}

      {step === 'checklist' && (
        <section className={styles.card}>
          <h2 className={shared.sectionTitle}>{m.route_pc_checklist_heading({ period })}</h2>
          {checklist.isLoading && <p className={shared.subtle}>{m.route_pc_loading()}</p>}
          {checklist.data && <BlockerList blockers={checklist.data.blockers ?? []} />}
          <div className={styles.navRow}>
            <Button variant="ghost" onClick={() => setStep('select')}>
              {m.route_pc_btn_prev()}
            </Button>
            <Button
              onClick={() => setStep('review')}
              disabled={!canLock && ((checklist.data?.blockers ?? []).length ?? 0) > 0}
            >
              {m.route_pc_btn_next()}
            </Button>
          </div>
        </section>
      )}

      {step === 'review' && checklist.data && (
        <section className={styles.card}>
          <h2 className={shared.sectionTitle}>{m.route_pc_review_heading()}</h2>
          <dl className={styles.review}>
            <div>
              <dt>{m.route_pc_review_period()}</dt>
              <dd className={shared.mono}>{checklist.data.period}</dd>
            </div>
            <div>
              <dt>{m.route_pc_review_state()}</dt>
              <dd className={shared.mono}>{checklist.data.state}</dd>
            </div>
            <div>
              <dt>{m.route_pc_review_blockers()}</dt>
              <dd className={shared.mono}>{(checklist.data.blockers ?? []).length}</dd>
            </div>
            <div>
              <dt>{m.route_pc_review_can_lock()}</dt>
              <dd className={shared.mono}>{canLock ? m.route_pc_yes() : m.route_pc_no()}</dd>
            </div>
          </dl>
          <Input
            label={m.route_pc_note_label()}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={m.route_pc_note_placeholder()}
          />
          <div className={styles.navRow}>
            <Button variant="ghost" onClick={() => setStep('checklist')}>
              {m.route_pc_btn_prev()}
            </Button>
            <Button
              variant="primary"
              leadingIcon={<LockIcon size={14} />}
              disabled={!canLock || lockMut.isPending}
              loading={lockMut.isPending}
              onClick={() => lockMut.mutate()}
            >
              {m.route_pc_btn_lock_tier2()}
            </Button>
          </div>
          {!canLock && (
            <p className={styles.warn}>
              <AlertTriangle size={14} aria-hidden="true" /> {m.route_pc_blocked_warn()}
            </p>
          )}
        </section>
      )}

      {step === 'lock' && checklist.data && (
        <section className={[styles.card, styles.lockedBanner].join(' ')} aria-live="polite">
          <div className={styles.lockedHead}>
            <CheckCircle2 size={20} aria-hidden="true" />
            <h2 className={styles.lockedTitle}>{m.route_pc_locked_title()}</h2>
          </div>
          <dl className={styles.review}>
            <div>
              <dt>{m.route_pc_locked_period()}</dt>
              <dd className={shared.mono}>{checklist.data.period}</dd>
            </div>
            <div>
              <dt>{m.route_pc_locked_at()}</dt>
              <dd className={shared.mono}>{checklist.data.locked_at ?? '—'}</dd>
            </div>
            <div>
              <dt>{m.route_pc_locked_by()}</dt>
              <dd className={shared.mono}>{checklist.data.locked_by ?? '—'}</dd>
            </div>
          </dl>
          <h3 className={shared.sectionTitle}>{m.route_pc_audit_heading()}</h3>
          {(checklist.data.audit_trail ?? []).length === 0 ? (
            <EmptyState title={m.route_pc_audit_empty()} />
          ) : (
            <ul className={styles.audit}>
              {(checklist.data.audit_trail ?? []).map((e, i) => (
                <li key={i}>
                  <span className={shared.mono}>{e.at}</span>
                  <span>{e.actor}</span>
                  <span className={shared.mono}>{e.action}</span>
                  {e.note && <span className={shared.subtle}>{e.note}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {isLocked && step !== 'lock' && (
        <p className={styles.warn}>
          <LockIcon size={14} aria-hidden="true" /> {m.route_pc_already_locked_prefix()}{' '}
          <Link to="/evidence" className={shared.link}>
            {m.route_pc_already_locked_link()}
          </Link>
        </p>
      )}
    </div>
  );
}

function Stepper({ current }: { current: Step }): JSX.Element {
  const currentIdx = STEPS.findIndex((s) => s.id === current);
  return (
    <ol className={styles.stepper} aria-label={m.route_pc_stepper_aria()}>
      {STEPS.map((s, i) => (
        <li
          key={s.id}
          data-state={i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'pending'}
          aria-current={i === currentIdx ? 'step' : undefined}
        >
          {m.route_pc_step_label({ step: s.id })}
        </li>
      ))}
    </ol>
  );
}

function BlockerList({ blockers }: { blockers: PeriodCloseBlocker[] }): JSX.Element {
  if (blockers.length === 0) {
    return (
      <div className={styles.okBox}>
        <CheckCircle2 size={16} aria-hidden="true" /> {m.route_pc_blockers_ok()}
      </div>
    );
  }
  return (
    <ul className={styles.blockers}>
      {blockers.map((b) => (
        <li key={b.kind}>
          <AlertTriangle size={14} aria-hidden="true" />
          <span>{b.label}</span>
          <span className={shared.mono}>× {b.count}</span>
          {b.deeplink && (
            <a href={b.deeplink} className={shared.link}>
              {m.route_pc_blocker_deeplink()}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}
